import { Link } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { useAccess } from '@/hooks/useAccess';
import { Alert } from '@/components/ui';

/**
 * Says so, plainly, when the business is frozen.
 *
 * A refusal with no explanation is the worst version of this feature: a cashier
 * tries to ring up a sale, it fails, and nobody can tell them why. The banner
 * is shown to everyone, and it says something different to the person who can
 * do something about it.
 */
export function SubscriptionBanner() {
  const { isOwner } = useAccess();
  const subscription = useAppSelector((state) => state.tenant.context?.subscription);

  if (!subscription) return null;

  const periodEnd = subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd) : null;
  const trialEnd = subscription.trialEndsAt ? new Date(subscription.trialEndsAt) : null;
  const trialing = subscription.status === 'TRIALING';

  // The same test the API makes, so the banner and the refusal always agree.
  const lapsed =
    subscription.status === 'CANCELLED' ||
    subscription.status === 'EXPIRED' ||
    (trialing
      ? Boolean(trialEnd && trialEnd.getTime() < Date.now())
      : Boolean(periodEnd && periodEnd.getTime() < Date.now()));

  if (!lapsed) return null;

  return (
    <div className="mb-4">
      <Alert tone="danger" title="This business is read-only">
        {isOwner ? (
          <>
            The subscription has lapsed, so nobody else can record sales, payments or stock
            movements. You are not affected —{' '}
            <Link to="/app/admin/subscription" className="font-medium underline">
              renew it here
            </Link>{' '}
            to turn the business back on.
          </>
        ) : (
          <>
            The subscription has lapsed. You can still look things up, but nothing can be
            recorded until the owner renews it.
          </>
        )}
      </Alert>
    </div>
  );
}
